import { useCallback, useEffect, useState } from 'react';
import type { TextBlock } from '@/features/drawing/model';
import type {
  DraggingState,
  PotentialDragState,
  CanvasTransform,
} from './types';

interface UseTextBlockDragProps {
  activeBlockId: string | null;
  containerRef: React.RefObject<HTMLDivElement | null>;
  canvasTransform: CanvasTransform;
  updateTextBlock: (
    id: string,
    updates: Partial<Pick<TextBlock, 'x' | 'y'>>
  ) => void;
}

interface UseTextBlockDragReturn {
  dragging: DraggingState | null;
  potentialDrag: PotentialDragState | null;
  handleDragStart: (e: React.MouseEvent, block: TextBlock) => void;
  handleTouchDragStart: (e: React.TouchEvent, block: TextBlock) => void;
  startDragFromPotential: (
    block: TextBlock,
    potentialDrag: PotentialDragState
  ) => void;
  setPotentialDrag: React.Dispatch<
    React.SetStateAction<PotentialDragState | null>
  >;
  clearDrag: () => void;
}

export function useTextBlockDrag({
  activeBlockId,
  containerRef,
  canvasTransform,
  updateTextBlock,
}: UseTextBlockDragProps): UseTextBlockDragReturn {
  const [dragging, setDragging] = useState<DraggingState | null>(null);
  const [potentialDrag, setPotentialDrag] = useState<PotentialDragState | null>(
    null
  );

  // Handle drag start (mouse)
  const handleDragStart = useCallback(
    (e: React.MouseEvent, block: TextBlock) => {
      if (activeBlockId !== block.id) return;

      // Don't start drag if clicking on contenteditable
      const target = e.target as HTMLElement;
      if (
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      e.stopPropagation();

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      setDragging({
        blockId: block.id,
        startX: e.clientX,
        startY: e.clientY,
        startBlockX: block.x,
        startBlockY: block.y,
      });
    },
    [activeBlockId, containerRef]
  );

  // Handle drag start (touch)
  const handleTouchDragStart = useCallback(
    (e: React.TouchEvent, block: TextBlock) => {
      if (activeBlockId !== block.id) return;

      // Don't start drag if touching contenteditable
      const target = e.target as HTMLElement;
      if (
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      e.stopPropagation();

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const touch = e.touches[0];
      if (!touch) return;

      setDragging({
        blockId: block.id,
        startX: touch.clientX,
        startY: touch.clientY,
        startBlockX: block.x,
        startBlockY: block.y,
      });
    },
    [activeBlockId, containerRef]
  );

  // Start drag from potential drag state (for mobile text block touch handling)
  const startDragFromPotential = useCallback(
    (block: TextBlock, potential: PotentialDragState) => {
      setDragging({
        blockId: block.id,
        startX: potential.startX,
        startY: potential.startY,
        startBlockX: block.x,
        startBlockY: block.y,
      });
      setPotentialDrag(null);
    },
    []
  );

  // Handle mouse move for dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;

      const dx = (e.clientX - dragging.startX) / canvasTransform.zoom;
      const dy = (e.clientY - dragging.startY) / canvasTransform.zoom;

      updateTextBlock(dragging.blockId, {
        x: dragging.startBlockX + dx,
        y: dragging.startBlockY + dy,
      });
    },
    [dragging, canvasTransform.zoom, updateTextBlock]
  );

  // Handle touch move for dragging
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!dragging) return;

      const touch = e.touches[0];
      if (!touch) return;

      const dx = (touch.clientX - dragging.startX) / canvasTransform.zoom;
      const dy = (touch.clientY - dragging.startY) / canvasTransform.zoom;

      updateTextBlock(dragging.blockId, {
        x: dragging.startBlockX + dx,
        y: dragging.startBlockY + dy,
      });
    },
    [dragging, canvasTransform.zoom, updateTextBlock]
  );

  // Clear drag state
  const clearDrag = useCallback(() => {
    setDragging(null);
  }, []);

  // Add global event listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', clearDrag);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', clearDrag);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', clearDrag);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', clearDrag);
      };
    }
    return undefined;
  }, [dragging, handleMouseMove, handleTouchMove, clearDrag]);

  return {
    dragging,
    potentialDrag,
    handleDragStart,
    handleTouchDragStart,
    startDragFromPotential,
    setPotentialDrag,
    clearDrag,
  };
}
